import keymirror from 'keymirror';
import { request } from 'js/api/axios';
import { callApi, noSchemaQuerySuccessMutator } from '../api';

import { SCHEMAS } from 'js/api/schemas';
import { spreadApiKeys, extractApiKeys } from '../utils';
import { QueryBase } from 'js/store/reducers/queries';

export const types = keymirror({
  ...spreadApiKeys('LOAD_ORGS'),
  ...spreadApiKeys('LOAD_ORG'),
  ...spreadApiKeys('LOAD_TEAM_SCORES'),
  ...spreadApiKeys('LOAD_QUESTION_SCORES'),
  ...spreadApiKeys('LOAD_ORG_RUBRIC_SCORES'),
  ...spreadApiKeys('LOAD_ORG_TRENDS'),
  ...spreadApiKeys('LOAD_TEAM_PARTICIPATION'),
  ...spreadApiKeys('ADD_TEAMS_TO_ORG'),
  ...spreadApiKeys('REMOVE_TEAM_FROM_ORG'),
  ...spreadApiKeys('CREATE_ORG'),
  ...spreadApiKeys('DELETE_ORG'),
  ...spreadApiKeys('UPDATE_ORG'),
  ...spreadApiKeys('ADD_CHILD_TO_ORG'),
  ...spreadApiKeys('REMOVE_CHILD_FROM_ORG'),
  ...spreadApiKeys('MOVE_CHILD_TO_ORG'),
  ...spreadApiKeys('MOVE_TEAM_TO_ORG'),
  ...spreadApiKeys('LIST_ORG_ASSESSMENT_HISTORY_SUMMARIES'),
  ...spreadApiKeys('LOAD_ORG_RUBRIC_WEIGHT_SCHEMES'),
  ...spreadApiKeys('UPDATE_ORG_RUBRIC_WEIGHT_SCHEME'),
  ...spreadApiKeys('LOAD_ORG_SNAPSHOT_SETS'),
  ...spreadApiKeys('LOAD_ORG_SNAPSHOT_SET'),
  ...spreadApiKeys('LOAD_ORG_SNAPSHOT'),
  ...spreadApiKeys('CREATE_ORG_SNAPSHOT'),
  ...spreadApiKeys('UPDATE_ORG_SNAPSHOT'),
  ...spreadApiKeys('LOAD_ORG_SNAPSHOT_SCORES'),
  ...spreadApiKeys('LOAD_ORG_SNAPSHOT_COMPARE'),
});

export function list(workspaceId) {
  return (dispatch) => {
    const meta = { queryId: `${QueryBase.WorkspaceOrgs}-${workspaceId}` };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORGS, types),
        request: (_data, token) =>
          request.get(`/api/workspaces/${workspaceId}/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function load(handle, queryId = null) {
  const meta = queryId ? { queryId } : {};

  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${handle}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { id: handle },
        schema: SCHEMAS.ORGANIZATION,
        meta,
      },
      dispatch
    );
  };
}

export function teamScores(orgId, assessmentKey) {
  return (dispatch) => {
    const meta = {
      queryId: `${QueryBase.TeamScores}-${orgId}-${assessmentKey}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_TEAM_SCORES, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/team-scores/${assessmentKey}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [
          undefined,
          //TODO: figure out a way where this is not necessry
          (action, _reqData, respData, status) => {
            return {
              ...action,
              status,
              data: { result: respData },
              meta,
            };
          },
          undefined,
        ],
      },
      dispatch
    );
  };
}

export function orgScoresQueryKey(orgId, sourceKey, query) {
  const breakoutKey = query?.breakout_id || 'combined';
  const endKey = query?.end_date || 'latest';
  return `${orgId}-${sourceKey}-${breakoutKey}-${endKey}`;
}

export function lookupQuestionScores(orgId, assessmentKey, query) {
  return (dispatch) => {
    const meta = {
      queryId: `${QueryBase.QuestionScores}-${orgScoresQueryKey(
        orgId,
        assessmentKey,
        query
      )}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_QUESTION_SCORES, types),
        request: (_data, token, currentWorkspaceId) =>
          request
            .get(
              `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/question-scores/${assessmentKey}`,
              {
                params: query,
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            .then((resp) => {
              const body = resp.data;
              if (!body || !Array.isArray(body.data)) {
                return resp;
              }

              return {
                ...resp,
                data: {
                  ...body,
                  data: {
                    teams: body.data,
                    catalog: body.catalog ?? { question_sets: [] },
                  },
                },
              };
            }),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function lookupOrgRubricWeightSchemes(orgId, rubricId, queryId = null) {
  return (dispatch) => {
    const meta = {
      queryId:
        queryId ??
        `org-rubric-weight-schemes/${orgId}/${encodeURIComponent(rubricId)}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_RUBRIC_WEIGHT_SCHEMES, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/rubric-weight-schemes/${encodeURIComponent(
              rubricId
            )}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function updateOrgRubricWeightScheme(orgId, rubricId, teamId, body) {
  return (dispatch) => {
    const meta = {
      queryId: `org-rubric-weight-schemes/${orgId}/${encodeURIComponent(
        rubricId
      )}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.UPDATE_ORG_RUBRIC_WEIGHT_SCHEME, types),
        request: (_data, token, currentWorkspaceId) =>
          request.patch(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/rubric-weight-schemes/${encodeURIComponent(
              rubricId
            )}`,
            {
              team_rubric_weight_scheme: {
                team_id: teamId,
                ...body,
              },
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function lookupOrgRubricScores(orgId, rubricId, query) {
  return (dispatch) => {
    const meta = {
      queryId: `${QueryBase.OrgRubricScores}-${orgScoresQueryKey(
        orgId,
        rubricId,
        query
      )}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_RUBRIC_SCORES, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/rubric-scores/${encodeURIComponent(
              rubricId
            )}`,
            {
              params: query,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function lookupOrgTrends(orgId, assessmentKey) {
  return (dispatch) => {
    const meta = {
      queryId: `${QueryBase.OrgTrends}-${orgId}-${assessmentKey}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_TRENDS, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/org-trends/${assessmentKey}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function lookupTeamParticipation(orgId, assessmentKey) {
  return (dispatch) => {
    const meta = {
      queryId: `${QueryBase.TeamParticipation}-${orgId}-${assessmentKey}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_TEAM_PARTICIPATION, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/team-participation/${assessmentKey}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function addTeamsToOrg(org, teamIds) {
  const data = { team_ids: teamIds };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.ADD_TEAMS_TO_ORG, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/organizations/${org.id}/team-associations`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data,
      },
      dispatch
    );
  };
}

export function removeTeamFromOrg(parentId, teamId) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.REMOVE_TEAM_FROM_ORG, types),
        request: (_data, token, currentWorkspaceId) =>
          request.delete(
            `/api/workspaces/${currentWorkspaceId}/organizations/${parentId}/team-associations/${teamId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
      },
      dispatch
    );
  };
}

export function addChildToOrg(parentId, childId) {
  const data = { child_org_id: +childId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.ADD_CHILD_TO_ORG, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/organizations/${parentId}/child-associations`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data,
      },
      dispatch
    );
  };
}

export function removeChildFromOrg(parentId, childId) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.REMOVE_CHILD_FROM_ORG, types),
        request: (_data, token, currentWorkspaceId) =>
          request.delete(
            `/api/workspaces/${currentWorkspaceId}/organizations/${parentId}/child-associations/${childId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
      },
      dispatch
    );
  };
}

export function moveChildToOrg(fromParentId, childOrgId, newParentId) {
  const data = { new_parent_id: newParentId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.MOVE_CHILD_TO_ORG, types),
        request: (data, token, currentWorkspaceId) =>
          request.put(
            `/api/workspaces/${currentWorkspaceId}/organizations/${fromParentId}/child-associations/${childOrgId}/move`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data,
      },
      dispatch
    );
  };
}

export function moveTeamToOrg(fromOrgId, teamId, newParentId) {
  const data = { new_parent_id: newParentId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.MOVE_TEAM_TO_ORG, types),
        request: (data, token, currentWorkspaceId) =>
          request.put(
            `/api/workspaces/${currentWorkspaceId}/organizations/${fromOrgId}/team-associations/${teamId}/move`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data,
      },
      dispatch
    );
  };
}

export function createOrg(organization, parentOrgId) {
  const data = { organization };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.CREATE_ORG, types),
        request: (data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/organizations`,
            data,
            {
              params: parentOrgId ? { parent: parentOrgId } : {},
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data,
      },
      dispatch
    );
  };
}

export function deleteOrg(organization) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.DELETE_ORG, types),
        request: (_data, token, currentWorkspaceId) =>
          request.delete(
            `/api/workspaces/${currentWorkspaceId}/organizations/${organization.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
      },
      dispatch
    );
  };
}

function orgWeightPlacementKey(row) {
  return `${row.parent_organization_id ?? ''}:${row.organization_id ?? ''}:${
    row.team_id ?? ''
  }`;
}

function mergeOrgWeightRows(existing, incoming) {
  if (!incoming?.length) {
    return existing ?? [];
  }

  const byKey = new Map(
    (existing ?? []).map((row) => [orgWeightPlacementKey(row), row])
  );

  for (const row of incoming) {
    byKey.set(orgWeightPlacementKey(row), {
      ...byKey.get(orgWeightPlacementKey(row)),
      ...row,
    });
  }

  return Array.from(byKey.values());
}

function buildOrganizationEntityPatch(orgId, patch) {
  return (prev = {}) => {
    const next = { ...prev, id: orgId };

    if (Object.prototype.hasOwnProperty.call(patch, 'weight_scheme')) {
      next.weight_scheme = patch.weight_scheme;
    }

    if (Object.prototype.hasOwnProperty.call(patch, 'org_weights')) {
      next.org_weights = mergeOrgWeightRows(
        prev.org_weights,
        patch.org_weights
      );
    }

    return next;
  };
}

function isWeightOnlyPatch(patch) {
  if (!patch || typeof patch !== 'object') {
    return false;
  }

  return Object.keys(patch).every(
    (key) => key === 'org_weights' || key === 'weight_scheme'
  );
}

export function updateOrg(id, patch) {
  const orgId = Number(id);
  const weightOnly = isWeightOnlyPatch(patch);

  return (dispatch) => {
    const apiCall = {
      types: extractApiKeys(types.UPDATE_ORG, types),
      request: (data, token, currentWorkspaceId) =>
        request.put(
          `/api/workspaces/${currentWorkspaceId}/organizations/${id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      data: { organization: patch },
      meta: {
        patch: {
          organizations: {
            [orgId]: buildOrganizationEntityPatch(orgId, patch),
          },
        },
      },
    };

    if (!weightOnly) {
      apiCall.schema = SCHEMAS.ORGANIZATION;
    }

    return callApi(apiCall, dispatch);
  };
}

export function listOrgSnapshotSets(orgId, queryId = null) {
  return (dispatch) => {
    const meta = {
      queryId: queryId ?? `${QueryBase.OrgSnapshotSets}-${orgId}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_SNAPSHOT_SETS, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function listOrgSnapshotsForSource(
  orgId,
  sourceType,
  sourceKey,
  query = {},
  queryId = null
) {
  return (dispatch) => {
    const archivedKey = query?.archived ? 'archived' : 'active';
    const meta = {
      queryId:
        queryId ??
        `${QueryBase.OrgSnapshotSet}-${orgId}-${sourceType}-${sourceKey}-${archivedKey}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_SNAPSHOT_SET, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots/${encodeURIComponent(
              sourceType
            )}/${encodeURIComponent(sourceKey)}`,
            {
              params: query,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function loadOrgSnapshot(orgId, snapshotId, queryId = null) {
  return (dispatch) => {
    const meta = {
      queryId: queryId ?? `${QueryBase.OrgSnapshot}-${orgId}-${snapshotId}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_SNAPSHOT, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots/${snapshotId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function createOrgSnapshot(orgId, body) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.CREATE_ORG_SNAPSHOT, types),
        request: (_data, token, currentWorkspaceId) =>
          request.post(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots`,
            body,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
      },
      dispatch
    );
  };
}

export function updateOrgSnapshot(orgId, snapshotId, body) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.UPDATE_ORG_SNAPSHOT, types),
        request: (_data, token, currentWorkspaceId) =>
          request.patch(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots/${snapshotId}`,
            body,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
      },
      dispatch
    );
  };
}

export function lookupOrgSnapshotCompare(
  orgId,
  sourceType,
  sourceKey,
  queryId = null
) {
  return (dispatch) => {
    const meta = {
      queryId:
        queryId ??
        `${QueryBase.OrgSnapshotCompare}-${orgId}-${sourceType}-${sourceKey}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_SNAPSHOT_COMPARE, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots/${encodeURIComponent(
              sourceType
            )}/${encodeURIComponent(sourceKey)}/compare`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function lookupOrgSnapshotScores(orgId, snapshotId, sourceType, query) {
  return (dispatch) => {
    const breakoutKey = query?.breakout_id || 'combined';
    const meta = {
      queryId: `${QueryBase.OrgSnapshotScores}-${orgId}-${sourceType}-${snapshotId}-${breakoutKey}`,
    };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_ORG_SNAPSHOT_SCORES, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/snapshots/${encodeURIComponent(
              sourceType
            )}/${snapshotId}/scores`,
            {
              params: query,
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function listAssessmentHistorySummaries(orgId) {
  const meta = {
    queryId: `${QueryBase.AssessmentHistorySummaries}-${orgId}`,
  };

  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys('LIST_ORG_ASSESSMENT_HISTORY_SUMMARIES', types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/organizations/${orgId}/assessment-history-summaries`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}
